import { api, ApiError, isApiError, type ApiErrorCode } from '../services/apiClient';
import { RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS } from './runningHubRetention';
import {
  isRunningHubListThumbUrl,
  stripInvalidRunningHubThumbUrlFromPayload,
} from './runningHubHistoryLocalStorage';
import {
  canSyncRunningHubHistoryItemToCloud,
  normalizeRunningHubHistoryOwnerEmail,
  purgeLegacyGlobalRunningHubHistoryKeysOnce,
  readGuestRunningHubHistoryFromLocalStorage,
  readRunningHubHistoryCacheFromLocalStorage,
  runningHubHistoryItemOwnedBy,
  stampRunningHubHistoryOwner,
  writeRunningHubHistoryListToLocalStorage,
  type RunningHubHistoryFeatureType,
} from './runningHubHistoryScope';

export type { RunningHubHistoryFeatureType };

/** 与后端 runninghub_feature_history POST 上限一致 */
const MAX_PAYLOAD_CHARS = 900_000;

const SLIM_STRIP_AUX_KEYS = new Set(['scenePanelWidthPx', 'scenePanelHeightPx']);

const SLIM_PROTECTED_KEY_SUBSTRINGS = [
  'image',
  'url',
  'cos',
  'render',
  'result',
  'output',
  'snapshot',
  'photo',
  'thumb',
  'panel',
  'source',
  'original',
  'upscale',
  'shadow',
  'main',
  'norm',
  'raw',
  'href',
  'link',
  'asset',
  'png',
  'jpg',
  'jpeg',
  'webp',
];

function isProtectedPayloadKey(key: string): boolean {
  const k = key.toLowerCase();
  return SLIM_PROTECTED_KEY_SUBSTRINGS.some((s) => k.includes(s));
}

function stripAuxFieldsDeep(val: unknown): unknown {
  if (val === null || val === undefined) return val;
  if (Array.isArray(val)) return val.map((x) => stripAuxFieldsDeep(x));
  if (typeof val === 'object') {
    const o = val as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(o)) {
      if (SLIM_STRIP_AUX_KEYS.has(key)) continue;
      out[key] = stripAuxFieldsDeep(o[key]);
    }
    return out;
  }
  return val;
}

export function slimPayloadForRunningHubCloud(
  featureType: RunningHubHistoryFeatureType,
  payload: unknown,
): unknown | null {
  const withinLimit = (p: unknown) => {
    try {
      return JSON.stringify(p).length <= MAX_PAYLOAD_CHARS ? p : null;
    } catch {
      return null;
    }
  };
  const first = withinLimit(payload);
  if (first !== null) return first;

  const stripped = stripAuxFieldsDeep(payload);
  const second = withinLimit(stripped);
  if (second !== null) return second;

  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const o = payload as Record<string, unknown>;
    const copy: Record<string, unknown> = { ...o };
    for (const key of Object.keys(copy)) {
      if (isProtectedPayloadKey(key)) continue;
      const v = copy[key];
      if (typeof v === 'string' && v.length > 80_000) {
        delete copy[key];
      }
    }
    const third = withinLimit(stripAuxFieldsDeep(copy));
    if (third !== null) return third;
  }

  return null;
}

export function extractRunningHubHistoryImageFieldsForLog(payload: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return out;
  const o = payload as Record<string, unknown>;
  for (const k of Object.keys(o)) {
    const v = o[k];
    if (typeof v === 'string' && (v.startsWith('http') || v.startsWith('data:image'))) {
      const s = v.length > 200 ? `${v.slice(0, 200)}…` : v;
      out[k] = s;
    }
    if (k === 'urls' && Array.isArray(v)) {
      out._urls_len = String(v.length);
      const first = v.find((x) => typeof x === 'string') as string | undefined;
      if (first) out._urls_first = first.length > 200 ? `${first.slice(0, 200)}…` : first;
    }
  }
  return out;
}

export type RunningHubHistoryLoadHooks = {
  userEmailForLog?: string | null;
  currentUserEmail?: string | null;
  /** @deprecated 已由 RunningHubHistoryLoadResult.errorCode / serverCode 取代；Step 5 会移除 */
  onCloudPullError?: (message: string) => void;
};

export type RunningHubHistoryLoadSource =
  | 'cloud'
  | 'memory_stale'   // 类型完备：loader 不主动产出，由 hook 选择内存快照时使用
  | 'cache_stale'
  | 'cloud_filtered_empty'
  | 'guest_local'
  | 'empty';

/** 与 GET /api/gemini/runninghub-feature-history diagnostics 对齐 */
export type RunningHubFeatureHistoryGetDiagnostics = {
  email_jwt?: string;
  email_normalized?: string;
  db_row_count?: number;
  returned_count?: number;
  filtered_owner_mismatch_count?: number;
  owner_stamped_count?: number;
  ownership_check_passed?: boolean;
  history_read_source?: 'rest_main' | 'rpc_fallback' | 'suspicious_empty';
  rest_rows_length?: number;
  rpc_rows_length?: number;
  runninghub_history_suspicious_empty?: boolean;
};

export function isCloudFilteredEmptyDiagnostics(
  diagnostics?: RunningHubFeatureHistoryGetDiagnostics | null,
): boolean {
  if (!diagnostics) return false;
  const db = diagnostics.db_row_count ?? 0;
  const returned = diagnostics.returned_count ?? 0;
  return db > 0 && returned === 0;
}

export function isRunningHubHistorySuspiciousEmptyDiagnostics(
  diagnostics?: RunningHubFeatureHistoryGetDiagnostics | null,
): boolean {
  return diagnostics?.runninghub_history_suspicious_empty === true;
}

/** 至少 minLen 位 base36 随机后缀（用于 clientItemId，与 storage 内 __guest__ 无关） */
function runningHubClientItemIdRandomSuffix(minLen = 8): string {
  let s = '';
  while (s.length < minLen) {
    s += Math.random().toString(36).slice(2);
  }
  return s.slice(0, minLen);
}

/**
 * 新记录 clientItemId：featureType-owner-timestamp-random（不改已有 opts.id / 云端旧 id）。
 * owner = 规范化邮箱或字面量 guest（无空格，不用 __guest__）。
 */
export function createScopedRunningHubClientItemId(
  featureType: RunningHubHistoryFeatureType,
  userEmail?: string | null,
  isAuthenticated?: boolean,
): string {
  const owner =
    isAuthenticated && userEmail
      ? normalizeRunningHubHistoryOwnerEmail(userEmail) || 'guest'
      : 'guest';
  const rand = runningHubClientItemIdRandomSuffix(8);
  return `${featureType}-${owner}-${Date.now()}-${rand}`;
}

/** Step 3：结构化云端拉取错误，承载 Step 1 ApiError 的 code/status/serverCode/message */
export type RunningHubCloudPullError = {
  /** 来自 ApiError.code；非 ApiError 时为 'unknown' */
  code: ApiErrorCode | 'unknown';
  /** HTTP status；fetch 失败 / auth_not_ready 时为 undefined */
  status?: number;
  /** 后端 body.code，如 'MISSING_TOKEN' */
  serverCode?: string;
  /** 显示文案（保留旧 error: string 的内容） */
  message: string;
};

export type RunningHubHistoryLoadResult<T> = {
  items: T[];
  source: RunningHubHistoryLoadSource;
  /** @deprecated 等价于 errorInfo.message；保留以兼容旧 hook applyLoaded 的字符串判断 */
  error?: string;
  errorCode?: ApiErrorCode | 'unknown';
  errorStatus?: number;
  serverCode?: string;
  runninghubHistorySuspiciousEmpty?: boolean;
};

export type PullRunningHubFeatureHistoryResult<T> = {
  items: T[];
  cloudCount: number;
  diagnostics?: RunningHubFeatureHistoryGetDiagnostics;
  /** @deprecated 等价于 errorInfo.message；保留兼容（utils/physicalReconstructOriginalImage 等旧调用方仅读 items） */
  error?: string;
  errorInfo?: RunningHubCloudPullError;
};

function toCloudPullError(e: unknown): RunningHubCloudPullError {
  if (isApiError(e)) {
    const apiErr = e as ApiError;
    return {
      code: apiErr.code,
      status: apiErr.status,
      serverCode: apiErr.serverCode,
      message: apiErr.message || apiErr.originalMessage || 'unknown error',
    };
  }
  const msg = e instanceof Error ? e.message : String(e);
  return { code: 'unknown', message: msg || 'unknown error' };
}

export async function pullRunningHubFeatureHistoryPayloads<T extends { id: string; timestamp: number }>(
  featureType: RunningHubHistoryFeatureType,
  isAuthenticated: boolean,
  currentUserEmail?: string | null,
): Promise<PullRunningHubFeatureHistoryResult<T>> {
  if (!isAuthenticated) {
    return { items: [], cloudCount: 0 };
  }
  try {
    const res = (await api.listRunningHubFeatureHistory(featureType)) as {
      items?: Array<{ payload?: T }>;
      diagnostics?: RunningHubFeatureHistoryGetDiagnostics;
    };
    const diagnostics = res?.diagnostics;
    const rows = Array.isArray(res?.items) ? res.items : [];
    const items: T[] = [];
    for (const r of rows) {
      const stripped = stripInvalidRunningHubThumbUrlFromPayload(r.payload);
      if (!stripped || typeof (stripped as T).id !== 'string') continue;
      const stamped = stampRunningHubHistoryOwner(
        stripped as Record<string, unknown>,
        currentUserEmail,
      );
      if (!stamped || !runningHubHistoryItemOwnedBy(stamped, currentUserEmail)) continue;
      items.push(stamped as T);
    }
    console.log('[runninghub-history-cloud] pull OK', {
      featureType,
      cloudRowCount: items.length,
      db_row_count: diagnostics?.db_row_count ?? null,
      returned_count: diagnostics?.returned_count ?? null,
      filtered_owner_mismatch_count: diagnostics?.filtered_owner_mismatch_count ?? null,
      history_read_source: diagnostics?.history_read_source ?? null,
      runninghub_history_suspicious_empty: diagnostics?.runninghub_history_suspicious_empty ?? null,
    });
    return { items, cloudCount: items.length, diagnostics };
  } catch (e: unknown) {
    const errorInfo = toCloudPullError(e);
    console.error('[runninghub-history-cloud] pull FAILED', {
      featureType,
      code: errorInfo.code,
      status: errorInfo.status,
      serverCode: errorInfo.serverCode,
      message: errorInfo.message,
    });
    return { items: [], cloudCount: 0, error: errorInfo.message, errorInfo };
  }
}

// ----------------------------------------------------------------------------
// Step 3：in-flight 去重
// 模块级 Map，按 (featureType, owner) 复用 Promise；finally 释放，失败 Promise 也释放。
// ----------------------------------------------------------------------------
const inFlightLoadByKey = new Map<string, Promise<RunningHubHistoryLoadResult<unknown>>>();

function buildInFlightKey(
  featureType: RunningHubHistoryFeatureType,
  isAuthenticated: boolean,
  userEmail: string | null | undefined,
): string {
  const e = (userEmail || '').trim().toLowerCase();
  const owner = isAuthenticated ? e || '__no_email__' : '__guest__';
  return `${featureType}::${owner}`;
}

/**
 * 登录用户：历史仅来自云端 GET（JWT user_email）；成功后写入 scoped localStorage 作 UI cache。
 * 禁止 merge / local 回写云端 / guest 并入登录用户。
 * 未登录：仅 guest scoped localStorage。
 *
 * Step 3：增加同 (featureType, owner) 的 in-flight Promise 复用，避免 React StrictMode
 * 双触发 / reset + email effect 同时打的重复请求。
 */
export async function loadRunningHubHistoryForDisplay<T extends { id: string; timestamp: number }>(
  featureType: RunningHubHistoryFeatureType,
  isAuthenticated: boolean,
  userEmail: string | null | undefined,
  cleanExpired: (items: T[]) => T[],
  hooks?: RunningHubHistoryLoadHooks,
): Promise<RunningHubHistoryLoadResult<T>> {
  const key = buildInFlightKey(featureType, isAuthenticated, userEmail);
  const existing = inFlightLoadByKey.get(key);
  if (existing) {
    console.log('[runninghub-history-cloud] reuse in-flight', { key });
    return existing as Promise<RunningHubHistoryLoadResult<T>>;
  }
  const promise = doLoadRunningHubHistoryForDisplay<T>(
    featureType,
    isAuthenticated,
    userEmail,
    cleanExpired,
    hooks,
  );
  // 同时存 unknown 视图便于 Map 类型统一；释放时用 === 校验避免覆盖后被误删
  const tracked = promise as unknown as Promise<RunningHubHistoryLoadResult<unknown>>;
  inFlightLoadByKey.set(key, tracked);
  void promise.finally(() => {
    if (inFlightLoadByKey.get(key) === tracked) {
      inFlightLoadByKey.delete(key);
    }
  });
  return promise;
}

async function doLoadRunningHubHistoryForDisplay<T extends { id: string; timestamp: number }>(
  featureType: RunningHubHistoryFeatureType,
  isAuthenticated: boolean,
  userEmail: string | null | undefined,
  cleanExpired: (items: T[]) => T[],
  hooks?: RunningHubHistoryLoadHooks,
): Promise<RunningHubHistoryLoadResult<T>> {
  purgeLegacyGlobalRunningHubHistoryKeysOnce();

  if (!isAuthenticated) {
    const guest = cleanExpired(readGuestRunningHubHistoryFromLocalStorage<T>(featureType));
    const sorted = guest
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS);
    return { items: sorted, source: sorted.length ? 'guest_local' : 'empty' };
  }

  const email = (hooks?.currentUserEmail || userEmail || '').trim();
  const pull = await pullRunningHubFeatureHistoryPayloads<T>(featureType, true, email);
  console.log('[runninghub-history-cloud] load cloud-only', {
    featureType,
    user_email: (hooks?.userEmailForLog || email).toLowerCase() || '(anonymous)',
    cloudRowCount: pull.cloudCount,
    db_row_count: pull.diagnostics?.db_row_count ?? null,
    returned_count: pull.diagnostics?.returned_count ?? null,
    history_read_source: pull.diagnostics?.history_read_source ?? null,
    runninghub_history_suspicious_empty: pull.diagnostics?.runninghub_history_suspicious_empty ?? null,
    errorCode: pull.errorInfo?.code ?? null,
    errorStatus: pull.errorInfo?.status ?? null,
    serverCode: pull.errorInfo?.serverCode ?? null,
    error: pull.error || null,
  });

  if (pull.errorInfo) {
    hooks?.onCloudPullError?.(pull.errorInfo.message);
    const cached = cleanExpired(readRunningHubHistoryCacheFromLocalStorage<T>(featureType, email));
    const stale = cached
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS);
    return {
      items: stale,
      source: stale.length ? 'cache_stale' : 'empty',
      error: pull.errorInfo.message,
      errorCode: pull.errorInfo.code,
      errorStatus: pull.errorInfo.status,
      serverCode: pull.errorInfo.serverCode,
    };
  }

  if (isRunningHubHistorySuspiciousEmptyDiagnostics(pull.diagnostics)) {
    console.warn('[runninghub-history-cloud] suspicious_empty keep local cache', {
      featureType,
      diagnostics: pull.diagnostics,
    });
    const cached = cleanExpired(readRunningHubHistoryCacheFromLocalStorage<T>(featureType, email));
    const stale = cached
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS);
    return {
      items: stale,
      source: stale.length ? 'cache_stale' : 'empty',
      runninghubHistorySuspiciousEmpty: true,
    };
  }

  if (isCloudFilteredEmptyDiagnostics(pull.diagnostics)) {
    console.warn('[runninghub-history-cloud] cloud_filtered_empty', {
      featureType,
      diagnostics: pull.diagnostics,
    });
    console.warn(
      '[runninghub-history-cloud] skip localStorage persistence due to ownership filtering',
      {
        featureType,
        diagnostics: pull.diagnostics,
      },
    );
    // IMPORTANT:
    // cloud_filtered_empty 禁止 writeRunningHubHistoryListToLocalStorage，
    // 否则未通过归属校验的污染 cache 会被永久化。
    // 此分支只允许 hook 使用 trustedByOwnerRef 内存快照临时展示。
    // 同样禁止 readRunningHubHistoryCacheFromLocalStorage 回填污染 cache。
    return { items: [], source: 'cloud_filtered_empty' };
  }

  const out = cleanExpired(pull.items)
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS);

  writeRunningHubHistoryListToLocalStorage(
    featureType,
    email,
    out as unknown as Record<string, unknown>[],
  );

  return { items: out, source: out.length ? 'cloud' : 'empty' };
}

export type RunningHubCloudSyncOptions = {
  userEmailForLog?: string | null;
  currentUserEmail?: string | null;
};

export type RunningHubCloudSyncResult = {
  ok: boolean;
  skipped?: boolean;
  error?: string;
  featureType: RunningHubHistoryFeatureType;
  clientItemId: string;
  imageFields?: Record<string, string>;
  thumbUrl?: string;
  originalImage?: string;
};

/** 仅用于当前会话新产生的条目显式存档（禁止 bulk / merge 触发） */
export async function syncRunningHubHistoryItemToCloud(
  featureType: RunningHubHistoryFeatureType,
  isAuthenticated: boolean,
  clientItemId: string,
  payload: unknown,
  options?: RunningHubCloudSyncOptions,
): Promise<RunningHubCloudSyncResult> {
  const base: RunningHubCloudSyncResult = {
    ok: false,
    featureType,
    clientItemId,
    imageFields: extractRunningHubHistoryImageFieldsForLog(payload),
  };
  if (!isAuthenticated || !clientItemId) {
    return { ...base, ok: true, skipped: true };
  }
  if (!canSyncRunningHubHistoryItemToCloud(payload, options?.currentUserEmail, isAuthenticated)) {
    console.warn('[runninghub-history-cloud] sync blocked — owner mismatch', {
      featureType,
      clientItemId,
      currentUser: (options?.currentUserEmail || '').trim().toLowerCase() || '(none)',
    });
    return { ...base, ok: true, skipped: true, error: 'owner_mismatch' };
  }
  const slim = slimPayloadForRunningHubCloud(featureType, payload);
  if (slim === null) {
    const err = 'payload 超过云端大小限制且无法安全瘦身（已保留所有图片相关字段）';
    console.warn('[runninghub-history-cloud] sync skip — slim null', {
      featureType,
      user_email: (options?.userEmailForLog || '').trim().toLowerCase() || '(unknown)',
      clientItemId,
      imageFields: base.imageFields,
    });
    return { ...base, error: err };
  }
  try {
    console.log('[runninghub-history-cloud] sync explicit session upsert', {
      featureType,
      user_email: (options?.userEmailForLog || '').trim().toLowerCase() || '(unknown)',
      clientItemId,
      imageFields: extractRunningHubHistoryImageFieldsForLog(slim),
    });
    const upsertRes = (await api.upsertRunningHubFeatureHistory(
      { featureType, clientItemId, payload: slim },
      { timeout: 180000 },
    )) as { thumbUrl?: string; originalImage?: string };
    const rawThumb = upsertRes?.thumbUrl;
    const thumbUrl =
      typeof rawThumb === 'string' && isRunningHubListThumbUrl(rawThumb) ? rawThumb.trim() : undefined;
    const rawOrig = upsertRes?.originalImage;
    const originalImage =
      typeof rawOrig === 'string' && /^https?:\/\//i.test(rawOrig.trim()) ? rawOrig.trim() : undefined;
    console.log('[runninghub-history-cloud] sync upsert OK', {
      featureType,
      clientItemId,
      hasThumbUrl: !!thumbUrl,
      hasOriginalImage: !!originalImage,
    });
    return {
      ...base,
      ok: true,
      thumbUrl,
      originalImage,
      imageFields: extractRunningHubHistoryImageFieldsForLog(slim),
    };
  } catch (e: any) {
    const msg = e?.message || String(e);
    console.error('[runninghub-history-cloud] sync upsert FAILED', {
      featureType,
      user_email: (options?.userEmailForLog || '').trim().toLowerCase() || '(unknown)',
      clientItemId,
      message: msg,
    });
    return { ...base, error: msg };
  }
}

export async function deleteRunningHubHistoryFromCloud(
  featureType: RunningHubHistoryFeatureType,
  isAuthenticated: boolean,
  clientItemId: string,
): Promise<void> {
  if (!isAuthenticated || !clientItemId) return;
  try {
    await api.deleteRunningHubFeatureHistoryByClient(featureType, clientItemId);
  } catch (e) {
    console.warn('[runninghub-history-cloud] delete failed', featureType, e);
  }
}
